import heProtocolsTranslations from '../../../i18n/locales/he/protocols.json';
import type {
  AssignmentRecord,
  DrugRecord,
  DripRecord,
  DrugSectionRecord,
  MedicationCase,
  MedicationRecord,
  ProtocolRecord,
  ResusConfigData,
  DefiEntry
} from '../types';

// The two raw shapes stored under AppConfig — one per configName:
//   { appName: 'RESUS', configName: 'protocols', hospital: H, data: RawProtocols }
//   { appName: 'RESUS', configName: 'medications', hospital: H, data: RawMedications }
// Both must be fetched before the wizard can be hydrated, because medications
// reference protocol IDs (in `medications.protocols[].protocolId`) — see
// `buildWizardData` below.

export interface RawProtocols {
  emergencyProtocols?: Array<{
    section: string;
    protocols?: Array<{
      id: string;
      name?: string;
      attachments?: Array<{ name?: string; file?: string }>;
    }>;
  }>;
}

export interface RawMedications {
  drugs?: any[];
  drips?: any[];
  sections?: Array<{ name: string; drugs?: string[] }>;
  protocols?: Array<{
    protocolId: string;
    drugs?: string[];
    drips?: string[];
    defi?: Array<{ name: string; joulePerKg: number }>;
    drugComments?: Record<string, string | undefined>;
  }>;
}

const CURRENT_SCHEMA_VERSION = 1 as const;

// Comments in the bundled defaults are sometimes i18n keys ("stroke.tpa.comment")
// that get resolved against the Hebrew translations bundle. Once the admin saves
// edits, comments are stored as literal Hebrew strings. The resolver is
// idempotent — any string that already looks like literal text passes through.
function resolveCommentToHebrew(maybeKey: string | undefined): string {
  if (!maybeKey) return '';
  const looksLikeKey = /^[a-z0-9._-]+$/i.test(maybeKey) && maybeKey.includes('.');
  if (!looksLikeKey) return maybeKey;
  const segments = maybeKey.split('.');
  let cur: any = heProtocolsTranslations;
  for (const seg of segments) {
    if (cur && typeof cur === 'object' && seg in cur) {
      cur = cur[seg];
    } else {
      return maybeKey;
    }
  }
  return typeof cur === 'string' ? cur : maybeKey;
}

function generateMedicationSlug(id: string): string {
  return id.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function cloneCases(cases: any): MedicationCase[] | null {
  if (!Array.isArray(cases) || cases.length === 0) return null;
  return cases.map((c: any) => ({
    predicate: { ...c.predicate },
    details: { ...c.details }
  }));
}

function toDrugRecord(drug: any): DrugRecord {
  return {
    id: drug.id,
    slug: generateMedicationSlug(drug.id),
    kind: 'drug',
    name: drug.name ?? '',
    howToGive: drug.howToGive ?? null,
    comment: drug.comment ?? null,
    warnText: drug.warnText ?? null,
    administrationInstructions: drug.administrationInstructions ?? null,
    type: drug.type ?? null,
    dose_per_kg: drug.dose_per_kg ?? null,
    dose_unit: drug.dose_unit ?? null,
    dose_range: drug.dose_range ?? null,
    concentration: drug.concentration ?? null,
    maxDose: drug.maxDose ?? null,
    minDose: drug.minDose ?? null,
    minDoseUnit: drug.minDoseUnit ?? null,
    constant_dose: drug.constant_dose ?? null,
    shouldDispalyConcentration: drug.shouldDispalyConcentration ?? null,
    cases: cloneCases(drug.cases)
  };
}

function toDripRecord(drip: any): DripRecord {
  return {
    id: drip.id,
    slug: generateMedicationSlug(drip.id),
    kind: 'drip',
    name: drip.name ?? '',
    howToGive: drip.howToGive ?? null,
    comment: drip.comment ?? null,
    warnText: drip.warnText ?? null,
    administrationInstructions: drip.administrationInstructions ?? null,
    calc_type: drip.calc_type ?? null,
    dose_unit: drip.dose_unit ?? null,
    allowed_dose_range: drip.allowed_dose_range ?? null,
    dose_per_kg_per_min: drip.dose_per_kg_per_min ?? null,
    dose_per_kg_per_hour: drip.dose_per_kg_per_hour ?? null,
    default_dilution_volume_ml: drip.default_dilution_volume_ml ?? null,
    default_dilution_volume_unit: drip.default_dilution_volume_unit ?? null,
    target_volume_ml_per_hour: drip.target_volume_ml_per_hour ?? null,
    existing_dilution_concentration: drip.existing_dilution_concentration ?? null,
    existing_dilution_concentration_dose_unit: drip.existing_dilution_concentration_dose_unit ?? null,
    max_dose_per_hour: drip.max_dose_per_hour ?? null,
    cases: cloneCases(drip.cases)
  };
}

export const EMPTY_WIZARD_DATA: ResusConfigData = {
  schemaVersion: CURRENT_SCHEMA_VERSION,
  protocols: [],
  medications: [],
  assignments: [],
  drugSections: [],
  defibrillation: {}
};

// Build the canonical wizard shape from the two raw AppConfig payloads.
// Both arguments are tolerated as null/undefined/{} — the server returns `{}`
// when neither the hospital nor the "apps" fallback has a record. Callers MUST
// resolve both fetches before invoking this; assignments cross-reference IDs
// from both raw configs, so a partial input would silently drop edges.
export function buildWizardData(
  medicationsRaw: RawMedications | null | undefined,
  protocolsRaw: RawProtocols | null | undefined
): ResusConfigData {
  const meds: RawMedications = medicationsRaw ?? {};
  const protos: RawProtocols = protocolsRaw ?? {};

  // 1) Protocols flattened from sectioned shape, keeping section as a property.
  const protocols: ProtocolRecord[] = [];
  for (const sectionEntry of protos.emergencyProtocols ?? []) {
    for (const p of sectionEntry.protocols ?? []) {
      protocols.push({
        id: p.id,
        slug: p.id,
        name: p.name ?? '',
        section: sectionEntry.section,
        attachments: Array.isArray(p.attachments)
          ? p.attachments
              .filter((a): a is { name?: string; file?: string } => !!a)
              .map(a => ({ name: a.name ?? '', file: a.file ?? '' }))
          : [],
        hidden: false
      });
    }
  }

  // 2) Medications: drugs first then drips, per the runtime display order.
  const medications: MedicationRecord[] = [
    ...(meds.drugs ?? []).map(toDrugRecord),
    ...(meds.drips ?? []).map(toDripRecord)
  ];

  const medicationIds = new Set(medications.map(m => m.id));
  const protocolIds = new Set(protocols.map(p => p.id));

  // 3) Assignments and defibrillation come from medications.protocols[*].
  // Defibrillation is preserved even for pseudo-protocols ("cpr") that have no
  // entry in the protocols config — those simply emit no assignments.
  const assignments: AssignmentRecord[] = [];
  const defibrillation: Record<string, DefiEntry[]> = {};

  for (const pcfg of meds.protocols ?? []) {
    if (Array.isArray(pcfg.defi) && pcfg.defi.length > 0) {
      defibrillation[pcfg.protocolId] = pcfg.defi.map(d => ({
        name: d.name,
        joulePerKg: d.joulePerKg
      }));
    }

    if (!protocolIds.has(pcfg.protocolId)) continue;

    const drugComments: Record<string, string | undefined> = pcfg.drugComments ?? {};
    let order = 0;

    for (const drugId of pcfg.drugs ?? []) {
      if (!medicationIds.has(drugId)) continue;
      assignments.push({
        protocolId: pcfg.protocolId,
        medicationId: drugId,
        order: order++,
        comment: resolveCommentToHebrew(drugComments[drugId])
      });
    }
    for (const dripId of pcfg.drips ?? []) {
      if (!medicationIds.has(dripId)) continue;
      assignments.push({
        protocolId: pcfg.protocolId,
        medicationId: dripId,
        order: order++,
        comment: resolveCommentToHebrew(drugComments[dripId])
      });
    }
  }

  // 4) Drug-browsing sections, dropping ids that no longer resolve.
  const drugSections: DrugSectionRecord[] = (meds.sections ?? []).map(s => ({
    name: s.name,
    medicationIds: (s.drugs ?? []).filter(id => medicationIds.has(id))
  }));

  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    protocols,
    medications,
    assignments,
    drugSections,
    defibrillation
  };
}

// Inverse of `buildWizardData`. Splits the wizard state into the two raw
// AppConfig payloads. Round-trip caveats:
//   - The raw shape separates `drugs` and `drips` arrays, so interleaved
//     ordering of mixed kinds inside a protocol is normalized to "drugs first,
//     then drips" on save. Relative order within each kind is preserved.
//   - Protocols are grouped by section in order of first appearance.
//   - Empty comments are stripped from `drugComments`; null fields on
//     medications are dropped to match the bundled JSON shape.
export function serializeWizardData(data: ResusConfigData): {
  protocolsRaw: RawProtocols;
  medicationsRaw: RawMedications;
} {
  // Group protocols by section, preserving order of first occurrence.
  const sectionOrder: string[] = [];
  const sectionMap = new Map<string, ProtocolRecord[]>();
  for (const p of data.protocols) {
    const section = p.section ?? '';
    if (!sectionMap.has(section)) {
      sectionMap.set(section, []);
      sectionOrder.push(section);
    }
    sectionMap.get(section)!.push(p);
  }

  const protocolsRaw: RawProtocols = {
    emergencyProtocols: sectionOrder.map(section => ({
      section,
      protocols: sectionMap.get(section)!.map(p => {
        const out: { id: string; name: string; attachments?: { name: string; file: string }[] } = {
          id: p.id,
          name: p.name
        };
        if (p.attachments && p.attachments.length > 0) {
          out.attachments = p.attachments.map(a => ({ name: a.name, file: a.file }));
        }
        return out;
      })
    }))
  };

  // Split medications back into drugs[] / drips[].
  const medKind = new Map<string, 'drug' | 'drip'>();
  const drugs: any[] = [];
  const drips: any[] = [];
  for (const m of data.medications) {
    medKind.set(m.id, m.kind);
    if (m.kind === 'drug') drugs.push(stripNulls(drugToRaw(m)));
    else drips.push(stripNulls(dripToRaw(m)));
  }

  // Group assignments by protocolId, ordered by `order`. Within each protocol,
  // emit drugs before drips (the raw shape can't represent interleaved order).
  const protoAssignments = new Map<string, AssignmentRecord[]>();
  for (const a of data.assignments) {
    if (!protoAssignments.has(a.protocolId)) protoAssignments.set(a.protocolId, []);
    protoAssignments.get(a.protocolId)!.push(a);
  }
  for (const list of protoAssignments.values()) {
    list.sort((a, b) => a.order - b.order);
  }

  // Union of protocolIds with assignments and protocolIds with defibrillation —
  // a protocol may have only defi (e.g. the "cpr" pseudo-protocol).
  const allProtoIds = new Set<string>([
    ...protoAssignments.keys(),
    ...Object.keys(data.defibrillation ?? {})
  ]);

  const medsProtocols: NonNullable<RawMedications['protocols']> = [];
  for (const protocolId of allProtoIds) {
    const assignmentsForProto = protoAssignments.get(protocolId) ?? [];
    const drugIds: string[] = [];
    const dripIds: string[] = [];
    const drugComments: Record<string, string> = {};

    for (const a of assignmentsForProto) {
      const kind = medKind.get(a.medicationId);
      if (kind === 'drug') drugIds.push(a.medicationId);
      else if (kind === 'drip') dripIds.push(a.medicationId);
      else continue; // medication was deleted; drop dangling assignment.
      if (a.comment && a.comment.trim() !== '') {
        drugComments[a.medicationId] = a.comment;
      }
    }

    const entry: NonNullable<RawMedications['protocols']>[number] = { protocolId };
    if (drugIds.length > 0) entry.drugs = drugIds;
    if (dripIds.length > 0) entry.drips = dripIds;
    if (Object.keys(drugComments).length > 0) entry.drugComments = drugComments;
    const defi = data.defibrillation?.[protocolId];
    if (defi && defi.length > 0) {
      entry.defi = defi.map(d => ({ name: d.name, joulePerKg: d.joulePerKg }));
    }
    medsProtocols.push(entry);
  }

  const medicationsRaw: RawMedications = {
    drugs,
    drips,
    sections: data.drugSections.map(s => ({
      name: s.name,
      drugs: s.medicationIds.filter(id => medKind.has(id))
    })),
    protocols: medsProtocols
  };

  return { protocolsRaw, medicationsRaw };
}

function drugToRaw(m: DrugRecord): Record<string, any> {
  return {
    id: m.id,
    name: m.name,
    howToGive: m.howToGive,
    comment: m.comment,
    warnText: m.warnText,
    administrationInstructions: m.administrationInstructions,
    type: m.type,
    dose_per_kg: m.dose_per_kg,
    dose_unit: m.dose_unit,
    dose_range: m.dose_range,
    concentration: m.concentration,
    maxDose: m.maxDose,
    minDose: m.minDose,
    minDoseUnit: m.minDoseUnit,
    constant_dose: m.constant_dose,
    shouldDispalyConcentration: m.shouldDispalyConcentration,
    cases: m.cases ?? undefined
  };
}

function dripToRaw(m: DripRecord): Record<string, any> {
  return {
    id: m.id,
    name: m.name,
    howToGive: m.howToGive,
    comment: m.comment,
    warnText: m.warnText,
    administrationInstructions: m.administrationInstructions,
    calc_type: m.calc_type,
    dose_unit: m.dose_unit,
    allowed_dose_range: m.allowed_dose_range,
    dose_per_kg_per_min: m.dose_per_kg_per_min,
    dose_per_kg_per_hour: m.dose_per_kg_per_hour,
    default_dilution_volume_ml: m.default_dilution_volume_ml,
    default_dilution_volume_unit: m.default_dilution_volume_unit,
    target_volume_ml_per_hour: m.target_volume_ml_per_hour,
    existing_dilution_concentration: m.existing_dilution_concentration,
    existing_dilution_concentration_dose_unit: m.existing_dilution_concentration_dose_unit,
    max_dose_per_hour: m.max_dose_per_hour,
    cases: m.cases ?? undefined
  };
}

// Drops keys whose value is null or undefined to keep stored documents close to
// the hand-authored bundled JSON shape.
function stripNulls<T extends Record<string, any>>(obj: T): T {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined) continue;
    out[k] = v;
  }
  return out as T;
}
